/**
 * Pure decision logic for chat push notification suppression.
 * Both the service worker and tests import from this file —
 * keep it free of DOM/SW-specific APIs (same convention as sw-messages.ts).
 */

// AIDEV-NOTE: data.url is set by backend ChatMessageContextBuilder.build() (chat_message.py)
// and must match the frontend chat route for push suppression to work in service-worker.ts.

/**
 * Compare a window client's full URL against the push payload's target chat path.
 *
 * The push payload sends a relative path like `/chat/alice-smith`.
 * We compare against the client's pathname to determine if it's
 * viewing the same conversation. Also handles pathname + search
 * defensively in case the backend ever includes query parameters.
 *
 * @returns true if the client is viewing the same chat the push targets
 */
export function isSameChatTarget(clientUrl: string, pushUrl: string): boolean {
  try {
    const parsed = new URL(clientUrl);
    return parsed.pathname === pushUrl || parsed.pathname + parsed.search === pushUrl;
  } catch {
    return false;
  }
}

export interface SuppressionResult {
  suppress: boolean;
  matchedClientUrl?: string;
}

/**
 * Determine whether a chat push notification should be suppressed because
 * a focused browser tab is already viewing the target conversation.
 *
 * This is a pure function: it takes pre-filtered focused client URLs and
 * the push data, and returns a decision. The caller (service worker) is
 * responsible for fetching clients and acting on the result.
 */
export function shouldSuppressChatNotification(
  focusedClientUrls: string[],
  pushData: { type?: string; url?: string },
): SuppressionResult {
  if (pushData.type !== 'chat_message' || !pushData.url) {
    return { suppress: false };
  }

  for (const clientUrl of focusedClientUrls) {
    if (isSameChatTarget(clientUrl, pushData.url)) {
      return { suppress: true, matchedClientUrl: clientUrl };
    }
  }

  return { suppress: false };
}
